import React, {FormEvent, useEffect, useMemo, useState} from 'react';
import Select, {MultiValue} from 'react-select';
import {IDtoOrdInvoice, IParsedInvoice, ISuggestedCreative, OnNumberChange} from "../types/types";
import {calcAmountFromIncluding, calcAmountFromExcluding} from "../utils/utils";
import {authFetch} from "../services/api";
import {toast} from "react-toastify";
import {API_URL} from "../constants/constants";
import {NumericFormat} from "react-number-format";
import SuggestedCreativesPanel from './SuggestedCreativesPanel'

type SuggestedCreativeOption = {
    value: string;
    label: string;
    data: ISuggestedCreative;
};

interface InvoiceFormProps {
    googleDocData: IParsedInvoice;
}

const InvoiceForm = ({googleDocData}: InvoiceFormProps) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [creatives, setCreatives] = useState<SuggestedCreativeOption[]>([]);
    const [selectedCreatives, setSelectedCreatives] = useState<SuggestedCreativeOption[]>([]);
    const [formData, setFormData] = useState<IDtoOrdInvoice>({
        googleDocId: '',
        contract_external_name: '',
        contract_external_id: '',
        date: '',
        date_end: '',
        date_start: '',
        serial: '',
        client_role: 'advertiser',
        contractor_role: 'publisher',
        amount: {
            services: {
                excluding_vat: 0,
                vat_rate: 0,
                vat: 0,
                including_vat: 0
            }
        },
        items: [
            {
                contract_external_id: '',
                amount: {
                    excluding_vat: 0,
                    vat_rate: 0,
                    vat: 0,
                    including_vat: 0

                },
                creatives: []
            },
        ],
    });


    useEffect(() => {
        const {
            type,
            invoice_number,
            invoice_date,
            invoice_nds_price,
            nds,
            contract_external_id,
            contract_external_name,
            googleDocId,
            suggested_creatives,
        } = googleDocData;

        const servicesAmount = calcAmountFromIncluding(invoice_nds_price, nds);

        const options: SuggestedCreativeOption[] = suggested_creatives.map((c) => ({
            value: c.creative_external_id,
            label: c.erid ? `${c.erid} — ${c.creative_external_name}` : c.creative_external_name,
            data: c,
        }));

        setCreatives(options);

        const earliestErid = suggested_creatives
            .map((c) => c.createdAt)
            .filter(Boolean)
            .sort()[0];
        const dateStart = earliestErid ? new Date(earliestErid).toISOString().slice(0, 10) : '';

        setFormData(prev => ({
            ...prev,
            type,
            date: invoice_date,
            date_start: dateStart,
            date_end: invoice_date,
            serial: `Акт № ${invoice_number}`,
            client_role: 'advertiser',
            contractor_role: 'publisher',
            amount: {
                services: servicesAmount,
            },
            contract_external_name,
            contract_external_id,
            items: [
                {
                    contract_external_id,
                    amount: calcAmountFromExcluding(0, nds),
                    creatives: [],
                },
            ],
            googleDocId,
        }));
    }, [googleDocData]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreativeSelect = (selectedOptions: MultiValue<SuggestedCreativeOption>) => {
        const selected = selectedOptions as SuggestedCreativeOption[];
        setSelectedCreatives(selected);

        setFormData(prev => ({
            ...prev,
            items: [
                {
                    ...prev.items[0],
                    creatives: selected.map((o) => ({
                        creative_external_id: o.data.creative_external_id,
                        creative_external_name: o.data.creative_external_name,
                    })),
                },
            ],
        }));
    };

    const handleToggleFromPanel = (creative: ISuggestedCreative) => {
        const existing = selectedCreatives.find(
            (o) => o.data.creative_external_id === creative.creative_external_id
        )
        const next: SuggestedCreativeOption[] = existing
            ? selectedCreatives.filter(
                  (o) => o.data.creative_external_id !== creative.creative_external_id
              )
            : [
                  ...selectedCreatives,
                  {
                      value: creative.creative_external_id,
                      label: creative.erid
                          ? `${creative.erid} — ${creative.creative_external_name}`
                          : creative.creative_external_name,
                      data: creative,
                  },
              ]
        handleCreativeSelect(next as MultiValue<SuggestedCreativeOption>)
    }

    const selectedIds = useMemo(
        () => new Set(selectedCreatives.map((o) => o.data.creative_external_id)),
        [selectedCreatives]
    )

    const handleServicesIncludingChange = ({floatValue}: OnNumberChange) => {
        const including = floatValue ?? 0;

        setFormData(prev => ({
            ...prev,
            amount: {
                services: calcAmountFromIncluding(including, prev.amount.services.vat_rate),
            },
        }));
    };

    const handleServicesVatRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rate = parseFloat(e.target.value) || 0;

        setFormData(prev => ({
            ...prev,
            amount: {
                services: calcAmountFromIncluding(prev.amount.services.including_vat, rate),
            },
        }));
    };

    const handleItemIncludingChange = ({floatValue}: OnNumberChange) => {
        const including = floatValue ?? 0;

        setFormData(prev => ({
            ...prev,
            items: [
                {
                    ...prev.items[0],
                    amount: calcAmountFromIncluding(including, prev.items[0].amount.vat_rate)
                },
            ],
        }));
    };

    const handleItemVatRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rate = parseFloat(e.target.value) || 0;

        setFormData(prev => ({
            ...prev,
            items: [
                {
                    ...prev.items[0],
                    amount: calcAmountFromIncluding(prev.items[0].amount.including_vat, rate)
                },
            ],
        }));
    };


    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setLoading(true)

        try {
            const response = await authFetch(`${API_URL}/ord/invoices`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.message)
            else setError('')

            toast.success('Данные успешно сохранены!');

        } catch (e) {
            console.error('Ошибка при отправке формы:', e);
            setError(`${e}`);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="invoice-layout">
            <div className="invoice-layout__form">
                <div className="form-wrapper">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">


                        <label htmlFor="serial" className="form-label">Номер акта *</label>
                        <input
                            type="text"
                            id="serial"
                            name="serial"
                            className="form-input"
                            value={formData.serial}
                            onChange={handleChange}
                            required
                        />

                        <label htmlFor="contract_external_name" className="form-label">Приложение</label>
                        <input
                            id="contract_external_name"
                            name="contract_external_name"
                            className="form-input"
                            value={formData.contract_external_name}
                            disabled
                        />

                        <label htmlFor="date_start" className="form-label">Дата начала периода *</label>
                        <input
                            type="date"
                            id="date_start"
                            name="date_start"
                            className="form-input"
                            value={formData.date_start}
                            onChange={handleChange}
                            required
                        />

                        <label htmlFor="date_end" className="form-label">Дата окончания периода *</label>
                        <input
                            type="date"
                            id="date_end"
                            name="date_end"
                            className="form-input"
                            value={formData.date_end}
                            onChange={handleChange}
                            required
                        />

                        <label htmlFor="date" className="form-label">Дата выставления *</label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            className="form-input"
                            value={formData.date}
                            onChange={handleChange}
                            required
                        />

                        <label htmlFor="amount.services.including_vat" className="form-label">Сумма с НДС *</label>
                        <NumericFormat
                            id="amount.services.including_vat"
                            name="amount.services.including_vat"
                            className="form-input"
                            value={formData.amount.services.including_vat}
                            thousandSeparator=" "
                            decimalScale={2}
                            fixedDecimalScale
                            onValueChange={handleServicesIncludingChange}
                            required
                        />

                        <label htmlFor="amount.services.vat_rate" className="form-label">Ставка НДС (%) *</label>
                        <input
                            type="number"
                            id="amount.services.vat_rate"
                            name="amount.services.vat_rate"
                            className="form-input"
                            value={formData.amount.services.vat_rate}
                            onChange={handleServicesVatRateChange}
                            required
                            step="1"
                        />

                        <div className="hint">
                            <small>
                                Сумма без НДС:{' '}
                                <NumericFormat
                                    value={formData.amount.services.excluding_vat}
                                    displayType="text"
                                    thousandSeparator=" "
                                    decimalScale={2}
                                    fixedDecimalScale
                                />
                            </small>
                        </div>

                        <div className="hint">
                            <small>
                                Сумма НДС:{' '}
                                <NumericFormat
                                    value={formData.amount.services.vat}
                                    displayType="text"
                                    thousandSeparator=" "
                                    decimalScale={2}
                                    fixedDecimalScale
                                />
                            </small>
                        </div>

                        <h3>Цена за размещение+маркировку</h3>

                        <label htmlFor="creative_external_id" className="form-label">Креатив *</label>
                        <Select
                            isMulti
                            options={creatives}
                            value={selectedCreatives}
                            onChange={handleCreativeSelect}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Выберите креатив..."
                            noOptionsMessage={() => "Креативы не найдены"}
                            isSearchable
                            required
                            id="creative_external_id"
                            name="creative_external_id"
                        />

                        <label htmlFor="items[0].amount.including_vat" className="form-label">Сумма с НДС *</label>
                        <NumericFormat
                            id="items[0].amount.including_vat"
                            name="items[0].amount.including_vat"
                            className="form-input"
                            value={formData.items[0].amount.including_vat || ''}
                            thousandSeparator=" "
                            decimalScale={2}
                            fixedDecimalScale
                            onValueChange={handleItemIncludingChange}
                            required
                        />
                        <div className="hint">
                            <small>Пожалуйста, введите стоимость маркировки из Акта</small>
                        </div>

                        <label htmlFor="items[0].amount.vat_rate" className="form-label">Ставка НДС (%) *</label>
                        <input
                            type="number"
                            id="items[0].amount.vat_rate"
                            name="items[0].amount.vat_rate"
                            className="form-input"
                            value={formData.items[0].amount.vat_rate}
                            onChange={handleItemVatRateChange}
                            required
                            step="1"
                        />

                        <div className="hint">
                            <small>
                                Сумма без НДС:{' '}
                                <NumericFormat
                                    value={formData.items[0].amount.excluding_vat}
                                    displayType="text"
                                    thousandSeparator=" "
                                    decimalScale={2}
                                    fixedDecimalScale
                                />
                            </small>
                        </div>

                        <div className="hint">
                            <small>
                                Сумма НДС:{' '}
                                <NumericFormat
                                    value={formData.items[0].amount.vat}
                                    displayType="text"
                                    thousandSeparator=" "
                                    decimalScale={2}
                                    fixedDecimalScale
                                />
                            </small>
                        </div>


                        <div className="error-message">{error}</div>

                        <button
                            type="submit"
                            className={`button ${loading ? 'loading' : ''}`}
                            disabled={loading}
                        >
                            {!loading && 'Отправить'}
                        </button>
                        </div>
                    </form>
                </div>
            </div>
            <div className="invoice-layout__panel">
                <SuggestedCreativesPanel
                    creatives={googleDocData.suggested_creatives}
                    selectedIds={selectedIds}
                    onToggle={handleToggleFromPanel}
                />
            </div>
        </div>
    );
};

export default React.memo(InvoiceForm);
